/*
 * Local seat-record resolution for TICKET-ID-KEYED surfaces (Duncan rulings
 * BOARDROOM-2026-09-02-150714 items 2 + 5).
 *
 * The assign store stamps records by SEAT KEY ({orderRef}|{ticketIndex}.
 * {seatIndex}); the Get-ready surface is keyed by my-api ticket ROW id. New
 * records carry `ticketId` directly (persistLocalOutcome stamps it at assign
 * time); records written before the stamp existed are resolved here through
 * the order's OWN allocations read — the SAME applyAllocations mapping the
 * Orders screen renders with, so the two surfaces provably agree on which row
 * a seat key means — and then BACKFILLED onto the record.
 *
 * Outputs:
 *  - childByTicketId — the child-identity overlay (modes child/guestchild/
 *    teen): the CHILD's name + managed-profile childKey per ticket row, so a
 *    child ticket whose SERVER holder is kind='invitee'/claimed (farmed to a
 *    parent email, claimed pre-slice) or kind-absent still renders the
 *    CHILD's name — the "For Duncan Francis" defect shape.
 *  - waiting — the buyer-side parent-signing visibility (mode 'guestparent'):
 *    the wire's holder.state for the farmed seat. HONESTY RULE: only what the
 *    allocations wire actually says — 'invited' (not yet claimed), 'claimed'
 *    (in the parent's hands; waiver/profile completeness is NOT on this
 *    wire), and 'ready'/'consumed' as the done signal WHEN the wire exposes
 *    it (tolerant read — see the PR note). A revoked/absent holder yields NO
 *    entry (stale local record — honest omission, never a fabricated state).
 *
 * BEST-EFFORT throughout: any per-order read failure simply resolves fewer
 * records (the surfaces fall back to their existing honest rendering) — a
 * degraded allocations read must never blank Get-ready.
 */
import { fetchOrderAllocations, type OrderAllocations } from '../../data/myApi'
import type { Order, Ticket } from '../../data/types'
import { applyAllocations } from './ordersModel'
import { seatKeyFor } from './ordersView'
import {
  getAssignStoreSnapshot, isChildIdentityMode, localAssignmentDisplayName,
  stampLocalAssignmentTicketId, type LocalAssignment,
} from './assignStore'

export interface LocalChildOverlay {
  /** The CHILD's display name (fresh ChildRecord facts win over the
   *  assignment-time snapshot). '' = recorded-as-child but unnamed — the
   *  consumer must OMIT the name line, never fall back to the guardian. */
  name: string
  /** Managed-profile key — the Edit-follows-the-holder route target. */
  childKey: string
}

export type WaitingSeatState = 'invited' | 'claimed' | 'done'

/** One farmed-to-parent seat, buyer-visible (item 5). */
export interface WaitingSeat {
  ticketId: string
  /** The owning order — the PURCHASER-scoped scan-token/QR routes (PR#125)
   *  are order-addressed: GET /me/orders/{orderId}/tickets/{ticketId}/… */
  orderId: string
  eventId: string
  eventName: string
  ticketTypeName: string | null
  eventStartsAt: string | null
  eventEndsAt: string | null
  /** The child's name off the local record ('' when unknown). */
  childName: string
  parentName: string
  parentEmail: string
  state: WaitingSeatState
  /** SERVER marker: the child's QR may show in the PURCHASER's account
   *  (BOARDROOM-2026-09-02-152856). Strictly `qrAvailable === true` off the
   *  wire — never derived locally (no signature → no QR anywhere). */
  qrAvailable: boolean
}

export interface ResolvedLocalRecords {
  childByTicketId: Map<string, LocalChildOverlay>
  waiting: WaitingSeat[]
}

/** orderRef part of a seat key ({orderRef}|{ti}.{si}); '' when malformed. */
export function orderRefFromSeatKey(seatKey: string): string {
  const bar = seatKey.lastIndexOf('|')
  return bar > 0 ? seatKey.slice(0, bar) : ''
}

/** Wire holder.state → the buyer-visible waiting state; null = no honest
 *  state to show (no holder / revoked / unknown vocabulary). */
export function waitingStateFromHolder(state: string | undefined | null): WaitingSeatState | null {
  switch (state) {
    case 'invited': return 'invited'
    case 'claimed': return 'claimed'
    // TOLERANT READ: 'ready'/'consumed' is the only completion signal this
    // wire carries; rendered as done WHEN present, never fabricated from
    // 'claimed' (waiver/profile completeness is not on the allocations wire).
    case 'ready':
    case 'consumed': return 'done'
    default: return null
  }
}

interface ResolveDeps {
  idToken: string
  base: string
  orders: Order[]
  ticketsForOrder: (orderId: string) => Ticket[]
  sessionEmail?: string
  fetchImpl?: typeof fetch
}

/** Resolve every relevant local seat record to its my-api ticket row.
 *  Fetches allocations ONLY for the orders that actually need a read (any
 *  guestparent record — the holder state is the point — plus child-identity
 *  records still missing their ticketId stamp). */
export async function resolveLocalSeatRecords(deps: ResolveDeps): Promise<ResolvedLocalRecords> {
  const { assigned } = getAssignStoreSnapshot()
  const records = Object.values(assigned)
  const childRecs = records.filter((r) => isChildIdentityMode(r.mode))
  const parentRecs = records.filter((r) => r.mode === 'guestparent')

  // Which orders need an allocations read this pass.
  const neededRefs = new Set<string>()
  for (const r of parentRecs) neededRefs.add(orderRefFromSeatKey(r.seatKey))
  for (const r of childRecs) if (!r.ticketId) neededRefs.add(orderRefFromSeatKey(r.seatKey))
  neededRefs.delete('')

  interface OrderRead {
    ref: string
    allocations: OrderAllocations
    /** seatKey → my-api ticket row id, via the SAME applyAllocations mapping
     *  Orders renders with (one source of truth for seat identity). */
    ticketIdBySeatKey: Map<string, string>
  }
  const reads = new Map<string, OrderRead>()
  await Promise.allSettled(
    [...neededRefs].map(async (ref) => {
      const order = deps.orders.find((o) => o.ref === ref)
      if (!order) return // box-office doesn't know this order — honest omission
      const allocations = await fetchOrderAllocations(
        deps.idToken, deps.base, order.id, deps.fetchImpl ?? fetch,
      )
      const merged = applyAllocations(deps.ticketsForOrder(order.id), allocations, deps.sessionEmail)
      const ticketIdBySeatKey = new Map<string, string>()
      merged.forEach((t, ti) => t.seats.forEach((s, si) => {
        if (s.ticketId) ticketIdBySeatKey.set(seatKeyFor(ref, ti, si), s.ticketId)
      }))
      reads.set(ref, { ref, allocations, ticketIdBySeatKey })
    }),
  )

  // RESOLVE-TIME REVALIDATION (review P2-2): whenever a fresh allocations
  // read is in hand for the record's order, the read's seat map WINS over the
  // stamp — a frozen-wrong stamp (index resolution drifted) is corrected
  // (silent re-stamp) rather than trusted; a seat the read no longer
  // materialises resolves null (never the freeze). Without a read in hand,
  // the stamp is the best honest knowledge and stands.
  const resolveTicketId = (r: LocalAssignment): string | null => {
    const read = reads.get(orderRefFromSeatKey(r.seatKey))
    if (read) {
      const expected = read.ticketIdBySeatKey.get(r.seatKey) ?? null
      if (expected && expected !== r.ticketId) stampLocalAssignmentTicketId(r.seatKey, expected)
      return expected
    }
    return r.ticketId ?? null
  }

  const childByTicketId = new Map<string, LocalChildOverlay>()
  for (const r of childRecs) {
    const tid = resolveTicketId(r)
    if (!tid) continue // unresolvable this pass — surfaces keep honest fallbacks
    childByTicketId.set(tid, { name: localAssignmentDisplayName(r), childKey: r.childKey })
  }

  const waiting: WaitingSeat[] = []
  for (const r of parentRecs) {
    const read = reads.get(orderRefFromSeatKey(r.seatKey))
    if (!read) continue // allocations unreadable — Orders row still shows it
    const tid = resolveTicketId(r)
    if (!tid) continue
    const row = read.allocations.tickets.find((x) => x.ticketId === tid)
    const wireState = waitingStateFromHolder(row?.holder?.state)
    if (!row || !wireState) continue // stale record / revoked — honest omission
    // STALE-PARENT GUARD (review P1-2): the LIVE holder must still be the
    // parent this record invited — a seat re-invited to a DIFFERENT adult
    // must not render "Waiting for {old parent}" (honest wire state instead:
    // the plain Orders invitee row, no waiting card).
    const liveEmail = (row.holder?.allocatedEmail ?? '').trim().toLowerCase()
    const recEmail = r.parentEmail.trim().toLowerCase()
    if (liveEmail && recEmail && liveEmail !== recEmail) continue
    // DONE flip (BOARDROOM-2026-09-02-152856 tolerant read): the server's
    // purchaser-visible completion marker wins over the holder-state
    // heuristic; absent (today's wire) = the wire-state mapping only.
    const state: WaitingSeatState = row.holderComplete === true ? 'done' : wireState
    waiting.push({
      ticketId: tid,
      orderId: read.allocations.orderId,
      eventId: read.allocations.eventId,
      eventName: read.allocations.eventName,
      ticketTypeName: (row.typeName || r.tType || '').trim() || null,
      eventStartsAt: read.allocations.eventStartsAt ?? null,
      eventEndsAt: read.allocations.eventEndsAt ?? null,
      childName: localAssignmentDisplayName(r),
      parentName: r.parentName.trim(),
      parentEmail: r.parentEmail.trim(),
      state,
      qrAvailable: row.qrAvailable === true,
    })
  }

  return { childByTicketId, waiting }
}
